import { useCallback } from "react";

import type { OptionStock_StockManagementQuery } from "@/lib/gql/__generated__/graphql";

import { useTransitionBlock } from "@/hooks/use-transition-block";

import {
	FeatureIslandContainer,
	FeatureIslandHeader,
	FeatureIslandMainWithBackGround,
} from "@/components/features/_layouts";
import { useIsSideMenuMobileRendered } from "@/components/features/side-menu/mobile";

import { HeaderUpdateButton, OptionStockManagementForLaptop, OptionStockManagementForMobile } from "./components";
import { useInitialized } from "../model/init";

// eslint-disable-next-line import/no-restricted-paths
import type { ApolloQueryResult } from "@apollo/client";

type Props = {
	hotelID: string;

	startDt: string;
	endDt: string;
	todaysDt: string;

	serverModel: OptionStock_StockManagementQuery;
	onFetchOptionStock: ({
		input: { startDt, endDt },
	}: {
		input: { startDt: string; endDt: string };
	}) => Promise<ApolloQueryResult<OptionStock_StockManagementQuery | undefined>>;
};

export const OptionStockManagementPresenter = ({
	hotelID: _hotelID,
	todaysDt,
	startDt,
	serverModel: _serverModel,
	onFetchOptionStock,
}: Props) => {
	const { serverModel, isValid, isDirty, onUpdateOptionStock, optionStockDiffStore, onRefetch } = useInitialized({
		serverModel: _serverModel,
		onFetchOptionStock,
	});

	const handleOnSelectYearOrMonth = useCallback(
		async (input: { startDt: string; endDt: string }) => {
			await onRefetch(input);
		},
		[onRefetch],
	);

	const isSideMenuMobileRendered = useIsSideMenuMobileRendered();

	useTransitionBlock(isDirty);

	return (
		<FeatureIslandContainer>
			<FeatureIslandHeader
				title="オプション在庫の設定"
				rightChildren={
					<HeaderUpdateButton isValid={isValid} isDirty={isDirty} onUpdateOptionStock={onUpdateOptionStock} />
				}
			/>

			<FeatureIslandMainWithBackGround>
				{isSideMenuMobileRendered ? (
					<OptionStockManagementForMobile
						serverModel={serverModel}
						optionStockDiffStore={optionStockDiffStore}
						todaysDt={todaysDt}
						startDt={startDt}
						handleOnSelectYearOrMonth={handleOnSelectYearOrMonth}
					/>
				) : (
					<OptionStockManagementForLaptop
						serverModel={serverModel}
						optionStockDiffStore={optionStockDiffStore}
						todaysDt={todaysDt}
						handleOnSelectYearOrMonth={handleOnSelectYearOrMonth}
					/>
				)}
			</FeatureIslandMainWithBackGround>
		</FeatureIslandContainer>
	);
};

OptionStockManagementPresenter.displayName = "OptionStockManagementPresenter";
